// apps/admin/src/features/audit/MultiSelectDropdown.tsx
//
// Round-13 (2026-05-23) — Lightweight checkbox multi-select dropdown for the
// admin audit-log filter bar. Replaces the previous chip-wall pattern (which
// rendered all 35 actions + 36 entity types as inline buttons and consumed
// huge vertical space on the page).
//
// Behaviour:
//   - Trigger button shows a compact summary: "Tất cả" when empty, label of
//     the single selected option when len==1, "<n> đã chọn" otherwise.
//   - Click trigger → toggle popover panel below the button.
//   - Panel contains: optional search filter, scroll list of checkboxes,
//     "Bỏ chọn tất cả" + "Đóng" buttons in the footer.
//   - Outside-click + Escape key + Tab-out close the popover.
//   - Controlled — caller owns the `value: string[]` + `onChange` callback.
//   - data-testid hooks so Playwright codegen can target by stable selector.
//
// No external deps — uses native DOM + Tailwind. Avoids pulling in
// @radix-ui/popover etc. for what is essentially a styled details/summary
// affordance.

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type JSX,
  type KeyboardEvent,
} from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectDropdownProps {
  /** Trigger button label shown above the dropdown. */
  label: string;
  options: ReadonlyArray<MultiSelectOption>;
  value: string[];
  onChange: (next: string[]) => void;
  /** Optional placeholder shown inside the trigger when value is empty. */
  placeholder?: string;
  /** Optional `data-testid` prefix. Default `multiselect`. */
  testId?: string;
  /** Disable interaction (e.g. while parent query is in flight). */
  disabled?: boolean;
}

export function MultiSelectDropdown({
  label,
  options,
  value,
  onChange,
  placeholder,
  testId = 'multiselect',
  disabled,
}: MultiSelectDropdownProps): JSX.Element {
  const { t } = useTranslation('ops');
  // Localized fallback when the caller omits an explicit placeholder.
  const effectivePlaceholder = placeholder ?? t('audit.multiSelect.all');
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const panelId = useId();

  // Outside-click + Escape handlers — registered only while panel is open
  // so the listeners don't run for every other dropdown on the page.
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(ev: MouseEvent): void {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(ev.target as Node)) {
        setOpen(false);
      }
    }
    function handleEsc(ev: globalThis.KeyboardEvent): void {
      if (ev.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  // Reset the inline search filter every time the panel closes so re-opening
  // shows the full list (matches the muscle-memory of native <select>).
  useEffect(() => {
    if (!open) setFilter('');
  }, [open]);

  const valueSet = useMemo(() => new Set(value), [value]);

  const filteredOptions = useMemo(() => {
    if (!filter) return options;
    const needle = filter.trim().toLowerCase();
    if (needle.length === 0) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(needle) ||
        o.value.toLowerCase().includes(needle),
    );
  }, [options, filter]);

  function toggle(optValue: string): void {
    if (valueSet.has(optValue)) {
      onChange(value.filter((v) => v !== optValue));
    } else {
      onChange([...value, optValue]);
    }
  }

  function clearAll(): void {
    onChange([]);
  }

  function triggerLabel(): string {
    if (value.length === 0) return effectivePlaceholder;
    if (value.length === 1) {
      const match = options.find((o) => o.value === value[0]);
      return match ? match.label : value[0]!;
    }
    return t('audit.multiSelect.selectedCount', { count: value.length });
  }

  function handleTriggerKey(ev: KeyboardEvent<HTMLButtonElement>): void {
    if (ev.key === 'ArrowDown' || ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      setOpen(true);
    }
  }

  return (
    <div className="relative flex flex-col gap-1" ref={containerRef}>
      <span className="text-xs font-medium text-pw-text-muted">{label}</span>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={panelId}
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
        onKeyDown={handleTriggerKey}
        data-testid={`${testId}-trigger`}
        className={[
          'inline-flex h-9 items-center justify-between gap-2 rounded-md border px-2 text-left text-sm',
          'bg-pw-bg text-pw-text transition-colors',
          'border-pw-border hover:bg-pw-surface',
          'disabled:opacity-50',
          open ? 'ring-2 ring-pw-primary/30' : '',
        ].join(' ')}
      >
        <span className="truncate">
          {value.length === 0 ? (
            <span className="text-pw-text-muted">{triggerLabel()}</span>
          ) : (
            triggerLabel()
          )}
        </span>
        <svg
          aria-hidden="true"
          viewBox="0 0 12 12"
          className={`h-3 w-3 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path
            d="M2 4l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open ? (
        <div
          id={panelId}
          role="listbox"
          aria-multiselectable="true"
          data-testid={`${testId}-panel`}
          className={[
            'absolute z-30 mt-1 flex max-h-[320px] w-full min-w-[240px] flex-col gap-1 rounded-md border border-pw-border bg-pw-bg p-2 shadow-lg',
            // Anchor under the trigger, position by parent flex layout.
            'top-full left-0',
          ].join(' ')}
        >
          {options.length > 8 ? (
            <input
              type="search"
              autoFocus
              placeholder={t('audit.multiSelect.filterPlaceholder')}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid={`${testId}-filter-input`}
              className="h-8 rounded-md border border-pw-border bg-pw-bg px-2 text-xs text-pw-text"
            />
          ) : null}
          <div className="flex flex-1 flex-col overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <span className="px-2 py-3 text-center text-xs text-pw-text-muted">
                {t('audit.multiSelect.noMatch')}
              </span>
            ) : (
              filteredOptions.map((opt) => {
                const checked = valueSet.has(opt.value);
                return (
                  <label
                    key={opt.value}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-sm text-pw-text hover:bg-pw-surface"
                    data-testid={`${testId}-option-${opt.value}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(opt.value)}
                      className="h-3.5 w-3.5 rounded border-pw-border accent-pw-primary"
                    />
                    <span className="flex-1 truncate">{opt.label}</span>
                    {opt.value !== opt.label ? (
                      <span className="font-mono text-[10px] text-pw-text-muted">
                        {opt.value}
                      </span>
                    ) : null}
                  </label>
                );
              })
            )}
          </div>
          <div className="flex items-center justify-between gap-2 border-t border-pw-border pt-1">
            <button
              type="button"
              onClick={clearAll}
              disabled={value.length === 0}
              data-testid={`${testId}-clear`}
              className="text-xs font-medium text-pw-primary hover:underline disabled:text-pw-text-muted disabled:no-underline"
            >
              {t('audit.multiSelect.clearAll')}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid={`${testId}-close`}
              className="rounded-md border border-pw-border bg-pw-bg px-2 py-0.5 text-xs font-medium text-pw-text hover:bg-pw-surface"
            >
              {t('audit.multiSelect.close')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default MultiSelectDropdown;
